/**
 * Page publique de redirection après un Stripe Checkout pour LOYER.
 * Utilisée par les locataires (non authentifiés) qui cliquent le lien
 * envoyé par leur propriétaire.
 *
 * URL : /stripe/checkout/result?session_id=cs_xxx[&cancelled=1]
 *
 * Stratégie :
 *  - Poll l'endpoint backend `GET /api/portal/immeuble/stripe/checkout/status/{session_id}`
 *    NOTE: cet endpoint nécessite l'auth portail. Pour une page PUBLIQUE
 *    accessible au locataire (qui n'a pas de compte), on ne polle PAS et
 *    on fait confiance au webhook côté serveur pour finaliser la mise à jour.
 *  - On affiche un message de succès ou d'annulation selon le query param.
 *  - Bouton pour retourner à la page d'accueil DG Comptabilité.
 */
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, AlertCircle, Mail } from "lucide-react";

export default function StripeCheckoutResult() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled") === "1";

  const isSuccess = !cancelled && !!sessionId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
        {/* Header tonal */}
        <div
          className={`px-6 py-8 text-center ${
            isSuccess
              ? "bg-gradient-to-br from-emerald-500 to-teal-600"
              : cancelled
              ? "bg-gradient-to-br from-amber-500 to-orange-600"
              : "bg-gradient-to-br from-rose-500 to-rose-600"
          }`}
        >
          <div className="w-16 h-16 mx-auto rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm mb-3">
            {isSuccess ? (
              <CheckCircle2 className="w-10 h-10 text-white" />
            ) : cancelled ? (
              <XCircle className="w-10 h-10 text-white" />
            ) : (
              <AlertCircle className="w-10 h-10 text-white" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-white">
            {isSuccess
              ? "Paiement reçu, merci !"
              : cancelled
              ? "Paiement annulé"
              : "Lien invalide"}
          </h1>
          <p className="text-white/90 text-sm mt-2">
            {isSuccess
              ? "Votre loyer a été acheminé à votre propriétaire."
              : cancelled
              ? "Vous pouvez retenter à tout moment depuis le lien reçu."
              : "Aucune session de paiement détectée."}
          </p>
        </div>

        {/* Corps */}
        <div className="px-6 py-6 space-y-4">
          {isSuccess && (
            <>
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm">
                <div className="font-semibold text-emerald-800 mb-1.5">
                  Que se passe-t-il maintenant ?
                </div>
                <ul className="text-emerald-700 text-[13px] space-y-1.5 leading-relaxed">
                  <li>• Un reçu Stripe vous a été envoyé par courriel.</li>
                  <li>
                    • Votre propriétaire verra automatiquement le mois marqué
                    « payé » dans son portail.
                  </li>
                  <li>
                    • Aucune information de paiement n'est partagée avec votre
                    propriétaire — Stripe sécurise la transaction.
                  </li>
                </ul>
              </div>

              {sessionId && (
                <div className="text-[10px] text-slate-400 font-mono break-all bg-slate-50 px-3 py-2 rounded-lg">
                  Référence Stripe : {sessionId}
                </div>
              )}
            </>
          )}

          {cancelled && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm">
              <div className="font-semibold text-amber-800 mb-1.5">
                Vous avez interrompu le paiement
              </div>
              <p className="text-amber-700 text-[13px] leading-relaxed">
                Aucun montant n'a été débité. Vous pouvez retourner au lien
                reçu par courriel pour réessayer, ou contacter votre
                propriétaire pour obtenir un nouveau lien.
              </p>
            </div>
          )}

          {!sessionId && !cancelled && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm">
              <p className="text-rose-700 text-[13px] leading-relaxed">
                Cette page est uniquement accessible via le lien de paiement
                envoyé par votre propriétaire. Vérifiez votre courriel ou
                contactez-le directement.
              </p>
            </div>
          )}

          <div className="border-t pt-4">
            <p className="text-[11px] text-slate-500 text-center mb-3 inline-flex items-center justify-center gap-1.5 w-full">
              <Mail className="w-3 h-3" />
              Une question ? Écrivez-nous à{" "}
              <a
                href="mailto:DGFiscalFlow@dgcomptabilite.ca"
                className="text-violet-600 hover:underline font-medium"
              >
                DGFiscalFlow@dgcomptabilite.ca
              </a>
            </p>
            <a
              href="/"
              className="block w-full text-center px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition"
              data-testid="stripe-result-home"
            >
              Retour à l'accueil
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t text-center">
          <p className="text-[10px] text-slate-400">
            Sécurisé par Stripe · Géré par DG Comptabilité
          </p>
        </div>
      </div>
    </div>
  );
}
